/**
 * Build banner logged once at startup. Mirrors apps/flash/src/utils/buildInfo.ts.
 *
 * The version and build date are NOT stored in source. The build reads
 * DASHBOARD_VERSION / DASHBOARD_BUILD_TYPE / DASHBOARD_BUILD_DATE from the
 * environment (Amplify Environment Variables + build spec) and
 * apps/dashboard/webpack.config.js mirrors them onto NX_PUBLIC_*, which Nx
 * inlines into the bundle via DefinePlugin. Bumping the version is therefore an
 * Amplify env-var change, not a commit.
 *
 * Locally (and in jest, where no build-time inlining happens) these are unset,
 * so the fallbacks below keep the banner meaningful without a build step.
 */
export const BUILD_VERSION = process.env.NX_PUBLIC_DASHBOARD_VERSION || 'local';
export const BUILD_TYPE =
  process.env.NX_PUBLIC_DASHBOARD_BUILD_TYPE || 'Development';
export const BUILD_DATE = process.env.NX_PUBLIC_DASHBOARD_BUILD_DATE || '';

const BADGE_STYLE =
  'color: #fff; background: #007bff; padding: 4px 8px; border-radius: 4px; font-weight: bold;';

// The box is drawn with box-drawing characters, so it only lines up in a
// monospace font — DevTools styles %c text with the page font otherwise, which
// would ragged the right-hand border. line-height keeps the rows from touching.
const BOX_STYLE = [
  'font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  'line-height: 1.5',
  'color: #007bff',
].join('; ');

/**
 * Render the build details as a bordered box. Pure and exported so the padding
 * can be asserted in tests without going through the console.
 */
export function formatBuildBox(rows: [string, string][]): string {
  const labelWidth = Math.max(...rows.map(([label]) => label.length));
  const contents = rows.map(
    ([label, value]) => `${label.padEnd(labelWidth)}   ${value}`
  );
  const width = Math.max(...contents.map((line) => line.length));
  const border = (left: string, right: string) =>
    `${left}${'─'.repeat(width + 4)}${right}`;

  return [
    border('╭', '╮'),
    ...contents.map((line) => `│  ${line.padEnd(width)}  │`),
    border('╰', '╯'),
  ].join('\n');
}

export function logBuildBanner() {
  const rows: [string, string][] = [
    ['Version', BUILD_VERSION],
    ['Build', BUILD_TYPE],
  ];
  // Only real (Amplify) builds stamp a date; skip the row entirely otherwise.
  if (BUILD_DATE) rows.push(['Date', BUILD_DATE]);

  console.log('%c Paymob Dashboard ', BADGE_STYLE);
  console.log(`%c${formatBuildBox(rows)}`, BOX_STYLE);
}
